import { Context, Controller } from '@hotwired/stimulus';

type TabElement = HTMLButtonElement | HTMLAnchorElement;

export default class TabsController extends Controller {
  private tablist: HTMLElement | null = null;
  private tabs!: NodeListOf<TabElement>;
  private tabPanels!: Array<HTMLElement>;
  private firstTab!: TabElement;
  private lastTab!: TabElement;

  constructor(context: Context) {
    super(context);

    this.onTabClick = this.onTabClick.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
  }

  connect(): void {
    this.tablist =
      this.element.closest("[role='tablist']") ||
      this.element.querySelector("[role='tablist']");

    if (!this.tablist) {
      throw new Error(
        'Expected role="tablist" to exist in data-controller="tabs" hierarchy',
      );
    }

    this.tabs = this.tablist.querySelectorAll('[role="tab"][aria-controls]');
    this.tabPanels = [];

    this.tabs.forEach((tab) => {
      this.tabPanels.push(
        document.getElementById(tab.getAttribute('aria-controls') || '')!,
      );
    });

    if (this.tabPanels.some((panel) => !panel)) {
      this.tablist = null;
      throw new Error(
        'Expected all role="tab" to point to an existing element via aria-controls',
      );
    }

    const currentTab = this.tablist.querySelector<TabElement>(
      '[aria-selected="true"]',
    )!;

    this.tabs.forEach((tab) => {
      tab.setAttribute(
        'tabindex',
        tab.getAttribute('aria-selected') === 'true' ? '0' : '-1',
      );

      tab.removeAttribute('disabled');
      this.onActivateTab(tab); // resets / disables fields
    });

    this.tabPanels.forEach((panel) => {
      if (panel.hasAttribute('hidden')) {
        panel.removeAttribute('tabindex');
      } else {
        panel.setAttribute('tabindex', '0');
      }
    });

    this.tablist.addEventListener('click', this.onTabClick);
    this.tablist.addEventListener('keydown', this.onKeyPress);

    this.firstTab = this.tabs.item(0);
    this.lastTab = this.tabs.item(this.tabs.length - 1);

    this.onActivateTab(currentTab);
  }

  disconnect(): void {
    if (!this.tablist) {
      return;
    }

    this.tablist.removeEventListener('click', this.onTabClick);
    this.tablist.removeEventListener('keydown', this.onKeyPress);
  }

  private onActivateTab(tab: TabElement) {
    if (tab.getAttribute('disabled')) {
      return;
    }

    // Follow link
    if (tab instanceof HTMLAnchorElement) {
      return;
    }

    // deselect old tab
    const oldTab = this.tablist!.querySelector('[aria-selected="true"]')!;
    oldTab.setAttribute('tabindex', '-1');
    oldTab.setAttribute('aria-selected', 'false');

    // deactivate old panel
    const oldPanel = document.getElementById(
      oldTab.getAttribute('aria-controls')!,
    )!;
    oldPanel.setAttribute('hidden', '');
    oldPanel.removeAttribute('tabindex');
    oldPanel
      .querySelectorAll('button,input,select,textarea')
      .forEach((input) => input.setAttribute('disabled', ''));

    // select new tab
    tab.setAttribute('tabindex', '0');
    tab.setAttribute('aria-selected', 'true');

    // activate new panel
    const newPanel = document.getElementById(
      tab.getAttribute('aria-controls')!,
    )!;
    newPanel.removeAttribute('hidden');
    newPanel.setAttribute('tabindex', '0');
    newPanel
      .querySelectorAll('button,input,select,textarea')
      .forEach((input) => input.removeAttribute('disabled'));
  }

  private onTabClick(event: Event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const tab = event.target.closest<TabElement>('[role="tab"]');
    if (!tab) {
      return;
    }

    this.onActivateTab(tab);
  }

  private onKeyPress(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight': {
        this.selectNext();
        event.preventDefault();
        break;
      }

      case 'ArrowUp':
      case 'ArrowLeft': {
        this.selectPrevious();
        event.preventDefault();
        break;
      }

      case 'Home': {
        this.selectFirst();
        event.preventDefault();
        break;
      }

      case 'End': {
        this.selectLast();
        event.preventDefault();
        break;
      }

      case 'Enter': {
        this.focusSelectedTabPanel();
        event.preventDefault();
        break;
      }
    }
  }

  private focusSelectedTabPanel() {
    const currentOption = this.tablist!.querySelector<TabElement>(
      `[aria-selected="true"]`,
    )!;
    const currentTabPanel = this.tabPanels.find(
      (panel) => panel.id === currentOption.getAttribute('aria-controls'),
    );
    if (currentTabPanel) {
      currentTabPanel.focus();
    }
  }

  private selectPrevious(currentOption?: TabElement) {
    currentOption ||= this.tablist!.querySelector<TabElement>(
      `[aria-selected="true"]`,
    )!;
    const previousOption = (currentOption.previousElementSibling ||
      this.lastTab) as TabElement;

    try {
      previousOption.focus();
    } catch (_) {
      // ignore error
    }

    if (document.activeElement !== previousOption) {
      this.selectPrevious(previousOption);
      return;
    }

    this.onActivateTab(previousOption);
  }

  private selectNext(currentOption?: TabElement) {
    currentOption ||= this.tablist!.querySelector<TabElement>(
      `[aria-selected="true"]`,
    )!;
    const nextOption = (currentOption.nextElementSibling ||
      this.firstTab) as TabElement;

    try {
      nextOption.focus();
    } catch (_) {
      // ignore error
    }

    if (document.activeElement !== nextOption) {
      this.selectNext(nextOption);
      return;
    }

    this.onActivateTab(nextOption);
  }

  private selectFirst() {
    this.selectNext(this.lastTab);
  }

  private selectLast() {
    this.selectPrevious(this.firstTab);
  }
}
